import React, { useCallback, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import logo from '../image/logo.png';
import type { PortalSession } from '../../../config/portalClient';
import '../PortalVirtual.css';

interface PortalLoginProps {
  enabled: boolean;
  basePath: string;
  badge: string;
  title: string;
  intro: string;
  hint: string;
  userLabel: string;
  userPlaceholder: string;
  disabledNotice: string;
  hasSession: () => boolean;
  login: (user: string, password: string) => Promise<PortalSession>;
}

const PortalLogin: React.FC<PortalLoginProps> = ({
  enabled, basePath, badge, title, intro, hint, userLabel, userPlaceholder, disabledNotice, hasSession, login,
}) => {
  const navigate = useNavigate();
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enabled) {
      setNotice(disabledNotice);
      return;
    }
    setLoading(true);
    setNotice('');
    try {
      const session = await login(user.trim(), password);
      navigate(session.mustChangePassword ? `${basePath}/inicio/contrasena` : `${basePath}/inicio`);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  }, [enabled, disabledNotice, login, user, password, navigate, basePath]);

  if (enabled && hasSession()) {
    return <Navigate to={`${basePath}/inicio`} replace />;
  }

  return (
    <div className="portal-page">
      <div className="portal-card">
        <div className="portal-info">
          <img src={logo} alt="CECAP" className="portal-logo" />
          <h1>CECAP <span>Maicao</span></h1>
          <p>{intro}</p>
        </div>

        <div className="portal-form-section">
          <span className="portal-badge">{badge}</span>
          <h2 className="portal-form-title">{title}</h2>
          <p className="portal-form-sub">{hint}</p>

          <form onSubmit={handleSubmit} className="portal-form">
            <div className="portal-field">
              <label htmlFor="portal-user">{userLabel}</label>
              <input
                type="text"
                id="portal-user"
                value={user}
                onChange={(e) => {
                  setUser(e.target.value);
                  if (notice) setNotice('');
                }}
                required
                placeholder={userPlaceholder}
                autoComplete="username"
              />
            </div>

            <div className="portal-field">
              <label htmlFor="portal-password">Contraseña</label>
              <input
                type="password"
                id="portal-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (notice) setNotice('');
                }}
                required
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            {notice && <div className="portal-error">{notice}</div>}

            <button type="submit" disabled={loading} className="portal-submit">
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>

          <div className="portal-back">
            <Link to="/portales">← Volver a portales</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;
